<script setup>
defineProps({
  company: {
    type: Object,
    required: true,
  },
  canEdit: {
    type: Boolean,
    default: false,
    required: false,
  },
})

const slideOverStore = useSlideOverStore()
const { extractMappedTopMotivations } = useAssessmentHelper()

const publicEmployeesCarousel = ref(null)

function getPersonalityScoresFromPublicEmployee(publicEmployee) {
  return publicEmployee.publicAssessments
    .find((assessment) => assessment?.type === 'personality')
    ?.data.find((data) => data.name === 'scores')
    ?.subdata?.reduce((acc, item) => {
      acc[item.name] = item.assessmentScore
      return acc
    }, {})
}

function getMotivationTopItemsFromPublicEmployee(publicEmployee) {
  return extractMappedTopMotivations(
    publicEmployee.publicAssessments.find(
      (assessment) => assessment?.type === 'motivations',
    ),
    3,
  )
}

function getCognitiveAssessmentFromPublicEmployee(publicEmployee) {
  return publicEmployee.publicAssessments.find(
    (assessment) => assessment?.type === 'cognitive',
  )
}
</script>

<template>
  <div
    v-if="company.about?.publicEmployees.length || canEdit"
    class="flex flex-col gap-5 rounded border border-gray-200 bg-white p-6 pt-8"
  >
    <div class="flex w-full justify-between">
      <h3 class="text-sans text-2xl text-secondary">Wie zijn wij</h3>
      <div v-if="company.about?.publicEmployees.length > 1" class="flex gap-2">
        <button
          class="flex h-10 w-10 items-center justify-center rounded-full bg-secondary"
          @click="publicEmployeesCarousel.prev()"
        >
          <IconArrow direction="left" class="h-4 w-4 text-white" />
        </button>
        <button
          class="flex h-10 w-10 items-center justify-center rounded-full bg-secondary"
          @click="publicEmployeesCarousel.next()"
        >
          <IconArrow direction="right" class="h-4 w-4 text-white" />
        </button>
      </div>
    </div>

    <Carousel
      key="public-employees-carousel"
      ref="publicEmployeesCarousel"
      :touch-drag="false"
      :mouse-drag="false"
      wrap-around
    >
      <Slide
        v-for="(publicEmployee, index) in company.about?.publicEmployees"
        :key="index"
      >
        <div class="flex w-full flex-col gap-4 mx-[1px]">
          <div class="flex flex-col gap-6 rounded-lg bg-gray-50 p-6">
            <div class="flex gap-4">
              <figure
                v-if="publicEmployee.candidate?.avatar"
                class="h-12 w-12 overflow-hidden rounded-full"
              >
                <BaseVisual :file="publicEmployee.candidate?.avatar" />
              </figure>
              <div class="flex flex-col">
                <span
                  v-if="publicEmployee.namePublic && publicEmployee.candidate"
                  class="text-lg font-semibold text-secondary"
                  >{{
                    `${publicEmployee.candidate?.firstName} ${publicEmployee.candidate?.lastName}`
                  }}</span
                >
                <span class="text-sm text-gray-600">{{
                  publicEmployee.function
                }}</span>
              </div>
            </div>
            <p class="whitespace-pre-wrap text-lg">
              {{ publicEmployee.description?.trim() }}
            </p>
          </div>

          <ExpandableItem
            v-if="getPersonalityScoresFromPublicEmployee(publicEmployee)"
            theme="block-transparent"
          >
            <template #title>
              <div class="flex items-center gap-4">
                <img
                  class="h-6 w-6"
                  src="/images/illustration-personality.svg"
                />
                <span class="text-lg font-semibold text-secondary">
                  Persoonlijkheid
                </span>
              </div>
            </template>
            <template #content>
              <AssessmentPersonalityDetailedScores
                class="mt-4"
                :initiative="
                  getPersonalityScoresFromPublicEmployee(publicEmployee)
                    ?.initiative
                "
                :influence="
                  getPersonalityScoresFromPublicEmployee(publicEmployee)
                    ?.influence
                "
                :relational="
                  getPersonalityScoresFromPublicEmployee(publicEmployee)
                    ?.relational
                "
                :structure="
                  getPersonalityScoresFromPublicEmployee(publicEmployee)
                    ?.structure
                "
              />
            </template>
          </ExpandableItem>

          <ExpandableItem
            v-if="
              getMotivationTopItemsFromPublicEmployee(publicEmployee)?.length
            "
            theme="block-transparent"
          >
            <template #title>
              <div class="flex items-center gap-4">
                <img
                  class="h-6 w-6"
                  src="/images/illustration-motivation.svg"
                />
                <span class="text-lg font-semibold text-secondary">
                  Wat mij drijft
                </span>
              </div>
            </template>
            <template #content>
              <div class="mt-6 flex flex-col gap-6">
                <div
                  v-for="(
                    motivation, motivationIndex
                  ) in getMotivationTopItemsFromPublicEmployee(publicEmployee)"
                  :key="motivationIndex"
                  class="flex flex-col gap-1"
                >
                  <span class="text-sm font-semibold text-gray-800">{{
                    motivation.label
                  }}</span>
                  <span class="text-sm text-gray-600">{{
                    motivation.description
                  }}</span>
                </div>
              </div>
            </template>
          </ExpandableItem>

          <ExpandableItem
            v-if="getCognitiveAssessmentFromPublicEmployee(publicEmployee)"
            theme="block-transparent"
          >
            <template #title>
              <div class="flex items-center gap-4">
                <img class="h-6 w-6" src="/images/illustration-cognitive.svg" />
                <span class="text-lg font-semibold text-secondary">
                  Cognitief
                </span>
              </div>
            </template>
            <template #content>
              <AssessmentCognitiveSmallScores
                :assessment="
                  getCognitiveAssessmentFromPublicEmployee(publicEmployee)
                "
                class="mt-6"
              />
            </template>
          </ExpandableItem>
        </div>
      </Slide>
    </Carousel>

    <Button
      v-if="canEdit"
      theme="primary"
      text="bewerken"
      class="w-fit"
      @click="slideOverStore.openSlideover('CompaniesEmployee')"
    />
  </div>
</template>
